import { Field } from 'formik'
import uniq from 'lodash-es/uniq'
import React, { useEffect, useMemo, useState } from 'react'

import {
  ButtonClean,
  CurrencyInput,
  Fieldset,
  Input,
  InputUI,
  RadioButton,
  Subsection,
  Textarea,
  useModalContext,
} from '@fourthwall/components'
import { useFormikContext } from '@fourthwall/utils/dist/formik'
import { pluralize } from '@fourthwall/utils/dist/string'

import { GenerateCodesModal } from '../../../../modules/GenerateCodesModal/GenerateCodesModal'
import type { FormValues, GiftCardGroup } from '../../GiftCard.types'
import { generateGiftCardCode } from '../../utils/generateGiftCardCode'
import { generateGiftCardCodes } from '../../utils/generateGiftCardCodes'
import { GiftCardCodesData } from '../GiftCardCodesData/GiftCardCodesData'
import * as S from './GiftCardDetails.styled'

type GiftCardDetailsProps = {
  disabled?: boolean
  giftCards?: GiftCardGroup['giftCards']
}

export const GiftCardDetails = ({ disabled, giftCards }: GiftCardDetailsProps) => {
  const { open } = useModalContext()
  const { values, touched, initialValues, errors, setFieldValue } = useFormikContext<FormValues>()
  const [codesInternalValue, setCodesInternalValue] = useState(initialValues.codes.join('\n'))

  useEffect(() => {
    setCodesInternalValue(initialValues.codes.join('\n'))
  }, [initialValues.codes])

  const handleCodeGenerate = () => {
    setFieldValue('code', generateGiftCardCode())
  }

  const handleCodesGenerate = () => {
    open(GenerateCodesModal, {
      title: 'Generate gift card codes',
      prefixExample: 'GIFT',
      onSubmit: (values) => {
        if (!values.quantity) {
          return
        }

        const codes = generateGiftCardCodes(values.quantity, values.prefix)

        setFieldValue('codes', codes)
        setCodesInternalValue(codes.join('\n'))
      },
    })
  }

  const handleCodesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCodesInternalValue(e.target.value)
    setFieldValue('codes', e.target.value.split('\n').filter(Boolean))
  }

  const codesErrors = useMemo(() => {
    if (!touched.codes) {
      return undefined
    }

    if (typeof errors.codes === 'object') {
      return uniq<string>(errors.codes).map((error) => <p>{error}</p>)
    }

    return errors.codes
  }, [errors.codes, touched.codes])

  const renderGiftCardsSuggestion = () => {
    return (
      <S.GiftCardsSuggestionWrapper>
        <div>
          Enter each code on its own line.{' '}
          {!disabled && (
            <ButtonClean
              label="Generate code for me"
              appearance="secondary"
              onClick={handleCodesGenerate}
            />
          )}
        </div>
        <div>
          {values.codes?.length} gift {values.codes && pluralize(values.codes.length, 'card')}
        </div>
      </S.GiftCardsSuggestionWrapper>
    )
  }

  const renderGiftCardAmount = () => {
    return (
      <Fieldset.Row>
        <Field
          component={CurrencyInput}
          name="amount"
          label="Gift card value"
          suggestion="Unused balance stays available for future purchases."
          fullWidth
          disabled={disabled}
        />
      </Fieldset.Row>
    )
  }

  const renderSingleGiftCard = () => {
    return (
      <>
        {renderGiftCardAmount()}
        <Fieldset.Row>
          <S.CodeFieldWrapper>
            <Field
              component={Input}
              name="code"
              label="Gift card code"
              displayError={false}
              disabled={disabled}
              fullWidth
            />
            <InputUI.Suggestion>
              Usable at checkout on any order.{' '}
              {!disabled && (
                <ButtonClean
                  label="Generate code for me"
                  appearance="secondary"
                  onClick={handleCodeGenerate}
                  disabled={disabled}
                />
              )}
            </InputUI.Suggestion>

            {errors.code && <InputUI.Error>{errors.code}</InputUI.Error>}
          </S.CodeFieldWrapper>
        </Fieldset.Row>
      </>
    )
  }

  const renderMultipleGiftCard = () => {
    return (
      <>
        {renderGiftCardAmount()}
        <Fieldset.Row>
          {!disabled && (
            <S.CodeFieldWrapper>
              <Textarea
                value={codesInternalValue}
                placeholder="Gift card codes (separated by line breaks)"
                label="Gift card codes (separated by line breaks)"
                rows={6}
                error={codesErrors}
                displayError={false}
                fullWidth
                disabled={disabled}
                onChange={handleCodesChange}
              />
              <InputUI.Suggestion>{renderGiftCardsSuggestion()}</InputUI.Suggestion>

              {codesErrors && <InputUI.Error>{codesErrors}</InputUI.Error>}
            </S.CodeFieldWrapper>
          )}
          {disabled && <GiftCardCodesData giftCards={giftCards} />}
        </Fieldset.Row>
      </>
    )
  }

  return (
    <Subsection title="Gift card details">
      <Fieldset>
        {!disabled && (
          <Fieldset.Row>
            <Field
              component={RadioButton}
              name="type"
              checkedValue="SINGLE"
              label="Single gift card"
              disabled={disabled}
            />
          </Fieldset.Row>
        )}

        {!disabled && values.type === 'SINGLE' && (
          <S.InnerContainer>{renderSingleGiftCard()}</S.InnerContainer>
        )}

        {disabled && values.type === 'SINGLE' && renderSingleGiftCard()}

        {!disabled && (
          <Fieldset.Row>
            <Field
              component={RadioButton}
              name="type"
              checkedValue="MULTIPLE"
              label="Multiple gift cards"
              disabled={disabled}
            />
          </Fieldset.Row>
        )}

        {!disabled && values.type === 'MULTIPLE' && (
          <S.InnerContainer>{renderMultipleGiftCard()}</S.InnerContainer>
        )}

        {disabled && values.type === 'MULTIPLE' && renderMultipleGiftCard()}
      </Fieldset>
    </Subsection>
  )
}
